import { useState, type ReactNode } from 'react';
import { useIsPhone } from './useIsPhone';
import {
  Cell,
  Column,
  ColumnResizer,
  ResizableTableContainer,
  Row,
  Table as AriaTable,
  TableBody,
  TableHeader,
  TableLayout,
  Virtualizer,
  type Selection,
  type SortDescriptor
} from 'react-aria-components';

export interface TableColumn<T> {
  id: string;
  label: string;
  isRowHeader?: boolean;
  allowsSorting?: boolean;
  /** Leave this column out of the stacked phone rows (a desktop-only action, say). */
  hideOnPhone?: boolean;
  render: (row: T) => ReactNode;
}

export interface TableProps<T extends { id: string }> {
  label: string;
  columns: TableColumn<T>[];
  rows: T[];
  sortDescriptor?: SortDescriptor;
  onSortChange?: (d: SortDescriptor) => void;
  selectionMode?: 'none' | 'single' | 'multiple';
  onSelectionChange?: (keys: Selection) => void;
  /** Turn on for long row sets. */
  virtualized?: boolean;
  density?: 'comfortable' | 'compact';
  /** Below `md`, render each row as a stacked card (the row-header column as its
   *  title, the rest as label/value lines) instead of clipping columns. Default true. */
  stackOnPhone?: boolean;
}

/** The phone rendering: one card per row, read top to bottom. Sorting and
 *  selection are desktop affordances and are not offered here. */
function StackedRows<T extends { id: string }>({ label, columns, rows }: Pick<TableProps<T>, 'label' | 'columns' | 'rows'>) {
  const header = columns.find((c) => c.isRowHeader) ?? columns[0];
  const rest = columns.filter((c) => c !== header && !c.hideOnPhone);
  return (
    <ul aria-label={label} className="m-0 p-0 list-none border-0 bg-surface font-ui text-ink">
      {rows.map((row) => (
        <li key={row.id} className="m-0 px-4 py-3 flex flex-col gap-1 border-0 border-b border-solid border-line last:border-b-0">
          {header ? <div className="m-0 p-0 text-md font-semibold text-ink">{header.render(row)}</div> : null}
          {rest.length > 0 ? (
            <dl className="m-0 p-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              {rest.map((c) => (
                <div key={c.id} className="contents">
                  <dt className="m-0 p-0 text-muted">{c.label}</dt>
                  <dd className="m-0 p-0 text-ink min-w-0">{c.render(row)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

// Every element sets its own border, margin, padding, background, font and
// appearance: nothing relies on Tailwind's preflight.
const container = 'react-aria-ResizableTableContainer m-0 p-0 border-0 bg-surface text-ink font-ui text-sm h-full max-h-full overflow-auto';
const table = 'm-0 p-0 border-0 bg-surface text-ink font-ui text-sm border-collapse w-full outline-none';
const headerGroup = 'm-0 p-0 border-0 bg-panel sticky top-0 z-bar';
const columnHeader =
  'm-0 p-0 border-0 border-b border-solid border-line bg-panel text-muted font-ui text-xs text-left font-bold relative cursor-default outline-none data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:-outline-offset-2';
const bodyRow =
  'm-0 p-0 border-0 bg-surface text-ink font-ui outline-none data-[hovered]:bg-panel data-[selected]:bg-panel data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:-outline-offset-2';
const cell = 'm-0 border-0 border-b border-solid border-line bg-transparent text-ink font-ui text-sm text-left align-middle outline-none truncate';
const resizer =
  'm-0 p-0 border-0 bg-transparent absolute top-0 right-0 h-full w-1 cursor-col-resize touch-none data-[resizing]:bg-line';

export function Table<T extends { id: string }>(props: TableProps<T>) {
  const { label, columns, rows, sortDescriptor: controlledSort, onSortChange, selectionMode = 'none', onSelectionChange, virtualized = false, density = 'comfortable', stackOnPhone = true } = props;
  const phone = useIsPhone();
  // Sorting is controlled when the caller passes a descriptor, else tracked here.
  const [ownSort, setOwnSort] = useState<SortDescriptor | undefined>(undefined);
  const sortDescriptor = controlledSort ?? ownSort;
  const handleSort = (d: SortDescriptor) => {
    if (controlledSort === undefined) setOwnSort(d);
    onSortChange?.(d);
  };
  const pad = density === 'compact' ? 'px-2 py-1' : 'px-3 py-2';
  if (phone && stackOnPhone) return <StackedRows label={label} columns={columns} rows={rows} />;
  const rowHeight = density === 'compact' ? 28 : 40;

  const body = (
    <AriaTable
      aria-label={label}
      className={table}
      sortDescriptor={sortDescriptor}
      onSortChange={handleSort}
      selectionMode={selectionMode}
      onSelectionChange={onSelectionChange}
    >
      <TableHeader className={headerGroup}>
        {columns.map((c) => (
          <Column
            key={c.id}
            id={c.id}
            isRowHeader={c.isRowHeader}
            allowsSorting={c.allowsSorting}
            className={`${columnHeader} ${pad}`}
          >
            {({ sortDirection }) => (
              <>
                <span className="m-0 p-0 border-0 bg-transparent">{c.label}</span>
                {sortDirection ? (
                  <span aria-hidden="true" className="m-0 p-0 border-0 bg-transparent">
                    {sortDirection === 'ascending' ? ' ▲' : ' ▼'}
                  </span>
                ) : null}
                <ColumnResizer className={resizer} />
              </>
            )}
          </Column>
        ))}
      </TableHeader>
      <TableBody items={rows} dependencies={[columns]} className="m-0 p-0 border-0 bg-surface">
        {(row: T) => (
          <Row id={row.id} columns={columns} className={bodyRow}>
            {(c: TableColumn<T>) => (
              <Cell className={`${cell} ${pad}`}>{c.render(row)}</Cell>
            )}
          </Row>
        )}
      </TableBody>
    </AriaTable>
  );

  return (
    <ResizableTableContainer className={container}>
      {virtualized ? (
        <Virtualizer layout={TableLayout} layoutOptions={{ rowHeight, headingHeight: rowHeight }}>
          {body}
        </Virtualizer>
      ) : (
        body
      )}
    </ResizableTableContainer>
  );
}
